import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import FileUpload from './FileUpload';
import { X, Phone, MapPin, Palette, MessageSquare, Zap, CalendarClock, Utensils, ShoppingBag } from 'lucide-react';

const ProfileModal = ({ vendor, onClose, onSuccess, updateVendorData }) => {
  const queryClient = useQueryClient();

  // Initialize editFormData with vendor data when component mounts
  const [editFormData, setEditFormData] = useState({
    name: vendor?.name || '',
    description: vendor?.description || '',
    phone: vendor?.phone || '',
    location: vendor?.location || '',
    banner_image_url: vendor?.banner_image_url || '',
    logo_url: vendor?.logo_url || '',
    theme_color: vendor?.theme_color || '#FF6B35',
    promo_message: vendor?.promo_message || '',
    social_links: vendor?.social_links || {},
    preparation_time_min: vendor?.preparation_time_min || 10,
    preparation_time_max: vendor?.preparation_time_max || 15,
    is_instant_delivery: vendor?.is_instant_delivery ?? true,
    is_schedule_delivery: vendor?.is_schedule_delivery ?? true,
    is_dine_in: vendor?.is_dine_in ?? true,
    accept_order: vendor?.accept_order ?? true, // Added accept_order field
  });

  // Handle input changes
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle toggle switches
  const handleToggle = (field) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const updateVendorMutation = useMutation(
    async (vendorData) => {
      const response = await api.put('/vendor/profile', vendorData);
      return response.data;
    },
    {
      onSuccess: (updatedVendorData) => {
        // Update the vendor data in AuthContext for real-time updates
        updateVendorData(updatedVendorData);

        // Also invalidate any React Query caches if they exist
        queryClient.invalidateQueries(['vendor-profile']);

        showSuccessToast('Profile updated successfully!', 'profile-update');

        // Call the onSuccess callback to close modal and reset state
        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to update profile',
          'profile-update'
        );
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    updateVendorMutation.mutate(editFormData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 mb-4 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Restaurant Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  required
                  className="input-field"
                  placeholder="Enter restaurant name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditInputChange}
                    className="input-field pl-10"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={editFormData.description}
                onChange={handleEditInputChange}
                rows="3"
                className="input-field"
                placeholder="Tell customers about your restaurant..."
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Location
              </label>
              <div className="relative">
                <MapPin className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  name="location"
                  value={editFormData.location}
                  onChange={handleEditInputChange}
                  className="input-field pl-10"
                  placeholder="Restaurant address"
                />
              </div>
            </div>
          </div>

          {/* Order Options Section */}
          <div>
            <h3 className="text-lg font-medium text-secondary-90  0 mb-4 border-b pb-2">Order Options</h3>
            <div className="space-y-4">
              {/* Accept Orders Toggle - Added as first option */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    editFormData.accept_order ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <ShoppingBag className={`h-5 w-5 ${
                      editFormData.accept_order ? 'text-green-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-900">Accept Orders</h4>
                    <p className="text-sm text-secondary-500">
                      {editFormData.accept_order 
                        ? 'Your restaurant is currently accepting orders' 
                        : 'Your restaurant is not accepting orders'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('accept_order')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    editFormData.accept_order ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="accept_order"
                    checked={editFormData.accept_order}
                    onChange={handleEditInputChange}
                    className="sr-only"
                  />
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editFormData.accept_order ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Instant Delivery Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-900">Instant Delivery</h4>
                    <p className="text-sm text-secondary-500">Customers can order for immediate delivery</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('is_instant_delivery')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    editFormData.is_instant_delivery ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="is_instant_delivery"
                    checked={editFormData.is_instant_delivery}
                    onChange={handleEditInputChange}
                    className="sr-only"
                  />
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editFormData.is_instant_delivery ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Schedule Delivery Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CalendarClock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-900">Schedule Delivery</h4>
                    <p className="text-sm text-secondary-500">Customers can schedule orders for later</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('is_schedule_delivery')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    editFormData.is_schedule_delivery ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="is_schedule_delivery"
                    checked={editFormData.is_schedule_delivery}
                    onChange={handleEditInputChange}
                    className="sr-only"
                  />
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editFormData.is_schedule_delivery ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Dine-in Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Utensils className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-900">Dine-in</h4>
                    <p className="text-sm text-secondary-500">Allow customers to dine in at your restaurant</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('is_dine_in')}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    editFormData.is_dine_in ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name="is_dine_in"
                    checked={editFormData.is_dine_in}
                    onChange={handleEditInputChange}
                    className="sr-only"
                  />
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editFormData.is_dine_in ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Media Section */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 mb-4 border-b pb-2">Media & Branding</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Banner Image
                </label>
                <FileUpload
                  onUploadSuccess={(file) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      banner_image_url: file.url,
                    }));
                  }}
                  onUploadError={(error) => {
                    console.error('Banner upload error:', error);
                  }}
                  multiple={false}
                  placeholder="Upload banner image"
                  showPreview={true}
                  existingImageUrl={editFormData.banner_image_url}
                />
                <input
                  type="hidden"
                  name="banner_image_url"
                  value={editFormData.banner_image_url}
                  onChange={handleEditInputChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Logo
                </label>
                <FileUpload
                  onUploadSuccess={(file) => {
                    setEditFormData((prev) => ({
                      ...prev,
                      logo_url: file.url,
                    }));
                  }}
                  onUploadError={(error) => {
                    console.error('Logo upload error:', error);
                  }}
                  multiple={false}
                  placeholder="Upload logo image"
                  showPreview={true}
                  existingImageUrl={editFormData.logo_url}
                />
                <input
                  type="hidden"
                  name="logo_url"
                  value={editFormData.logo_url}
                  onChange={handleEditInputChange}
                />
              </div>
            </div>
          </div>

          {/* Customization Section */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 mb-4 border-b pb-2">Customization</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Theme Color
                </label>
                <div className="flex items-center space-x-4">
                  <div className="relative flex-1">
                    <Palette className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                    <input
                      type="color"
                      name="theme_color"
                      value={editFormData.theme_color}
                      onChange={handleEditInputChange}
                      className="input-field pl-10 h-12"
                    />
                  </div>
                  <div 
                    className="h-10 w-10 rounded border"
                    style={{ backgroundColor: editFormData.theme_color }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Promo Message
                </label>
                <div className="relative">
                  <MessageSquare className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    name="promo_message"
                    value={editFormData.promo_message}
                    onChange={handleEditInputChange}
                    className="input-field pl-10"
                    placeholder="Special offer message"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preparation Time Section */}
          <div>
            <h3 className="text-lg font-medium text-secondary-900 mb-4 border-b pb-2">Preparation Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Preparation Time (Min)
                </label>
                <input
                  type="number"
                  name="preparation_time_min"
                  value={editFormData.preparation_time_min}
                  onChange={handleEditInputChange}
                  min="0"
                  className="input-field"
                  placeholder="10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Preparation Time (Max)
                </label>
                <input
                  type="number"
                  name="preparation_time_max"
                  value={editFormData.preparation_time_max}
                  onChange={handleEditInputChange}
                  min="0"
                  className="input-field"
                  placeholder="15"
                />
              </div>
            </div>
            <p className="text-sm text-secondary-500 mt-2">
              Estimated time range for order preparation in minutes
            </p>
          </div>

          {/* Summary of Selected Options */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-secondary-900 mb-2">Selected Order Options</h4>
            <div className="flex flex-wrap gap-2">
              {editFormData.accept_order ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <ShoppingBag className="h-3 w-3 mr-1" />
                  Accepting Orders
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <ShoppingBag className="h-3 w-3 mr-1" />
                  Not Accepting Orders
                </span>
              )}
              
              {editFormData.is_instant_delivery && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <Zap className="h-3 w-3 mr-1" />
                  Instant Delivery
                </span>
              )}
              
              {editFormData.is_schedule_delivery && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <CalendarClock className="h-3 w-3 mr-1" />
                  Schedule Delivery
                </span>
              )}
              
              {editFormData.is_dine_in && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Utensils className="h-3 w-3 mr-1" />
                  Dine-in
                </span>
              )}
              
              {!editFormData.accept_order && !editFormData.is_instant_delivery && !editFormData.is_schedule_delivery && !editFormData.is_dine_in && (
                <span className="text-sm text-red-600">No order options selected</span>
              )}
            </div>
            
            {/* Warning message when accept_order is false but other options are enabled */}
            {!editFormData.accept_order && (editFormData.is_instant_delivery || editFormData.is_schedule_delivery || editFormData.is_dine_in) && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                <p>⚠️ Other order options are enabled but your restaurant is not accepting orders.</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={updateVendorMutation.isLoading}
              style={{ backgroundColor: editFormData.theme_color }}
            >
              {updateVendorMutation.isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Update Profile'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>  
  );
};

export default ProfileModal;